// frontend/auth.tsx
import api from '../api';
import { getOrCreateDeviceId } from '../../helpers/device';
import { setRemainingSeconds } from './sessionTTL';

type JwtPayload = { id: number | string; email: string; role?: string; exp?: number; iat?: number };

function decodeJwt(token: string): JwtPayload | null {
    try {
        const base64 = token.split('.')[1];
        const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(json);
    } catch {
        return null;
    }
}

// 🔒 klientowa walidacja zgodna z backendem: min 8, ≥1 wielka litera, ≥1 cyfra
export function isStrongPassword(pw: string) {
    return typeof pw === 'string' && /^(?=.*[A-Z])(?=.*\d).{8,}$/.test(pw);
}

function isTokenExpired401(err: any) {
    const status = err?.response?.status;
    if (status == 401) {
        logoutUser();
    }
    
    const msg = String(err?.response?.data?.error || '').toLowerCase();
    return status === 401 && msg.includes('token expired');
}

export const registerUser = async (email: string, password: string, name?: string) => {
    try {
        // jeśli Twój axios ma baseURL='/api' to użyj '/users/register'
        const response = await api.post('/users/register', { email, password, name });
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.error || 'Registration failed');
    }
};

export const loginUser = async (email: string, password: string) => {
    try {
        // ⬇️ DODANE: nagłówki urządzenia (tak samo jak w Google login)
        const deviceId = getOrCreateDeviceId();
        const deviceName = navigator.userAgent || 'web';

        const response = await api.post(
            '/users/login',
            { email, password },
            {
                headers: {
                    'X-Device-Id': deviceId,
                    'X-Device-Name': deviceName,
                },
            }
        );

        const token: string = response.data.token;
        const payload = decodeJwt(token);

        // Panel dostępny dla admin i worker
        const role = payload?.role?.toLowerCase();
        const allowed = role === 'admin' || role === 'worker';
        if (!payload || !allowed) {
            throw new Error('Brak dostępu: wymagane uprawnienia administratora lub pracownika.');
        }

        localStorage.setItem('token', token);
        return token;
    } catch (error: any) {
        throw new Error(error.response?.data?.error || error.message || 'Login failed');
    }
};

export const getCurrentUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
        const response = await api.get('/users/me', {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (err: any) {
        if (isTokenExpired401(err)) {
            logoutUser();
        }
        return null;
    }
};

export const getToken = () => {
    return localStorage.getItem('token');
};

export const logoutUser = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('device_id');
    localStorage.removeItem('user');
    localStorage.removeItem('users_visibleCols');
    localStorage.removeItem('contents_visibleCols');
    setRemainingSeconds(null);
};

export const refreshToken = async () => {
    const res = await api.post('/auth/refresh');
    const token: string = res.data?.token;
    if (!token) throw new Error('Refresh failed');

    localStorage.setItem('token', token);

    const payload = decodeJwt(token);
    if (payload?.exp) {
        const rem = Math.max(0, Math.floor(payload.exp - Date.now() / 1000));
        setRemainingSeconds(rem);
    }

    return token; 
};

// === NOWE: reset/przypomnienie/ponowne wysłanie weryfikacji ===
export const forgotPassword = async (email: string) => {
    try {
        await api.post('/users/password/forgot', { email });
        return true;
    } catch (error: any) {
        throw new Error(error.response?.data?.error || 'Failed to initiate password reset');
    }
};

export const resetPassword = async (token: string, newPassword: string) => {
    try {
        await api.post('/users/password/reset', { token, new_password: newPassword });
        return true;
    } catch (error: any) {
        throw new Error(error.response?.data?.error || 'Password reset failed');
    }
};

export const resendVerification = async (email: string) => {
    try {
        await api.post('/users/resend-verification', { email });
        return true;
    } catch (error: any) {
        throw new Error(error.response?.data?.error || 'Resend failed');
    }
};
